import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Injector,
    InputSignal,
    InputSignalWithTransform,
    LOCALE_ID,
    Signal,
    WritableSignal,
    afterNextRender,
    booleanAttribute,
    computed,
    effect,
    inject,
    input,
    runInInjectionContext,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FieldTree, FormField, SchemaPathTree, disabled, email, form, maxLength, minLength, required } from '@angular/forms/signals';
import { RouterLink } from '@angular/router';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import {
    RtButtonDirective,
    RtCheckboxComponent,
    RtFieldComponent,
    RtInputComponent,
    RtSelectComponent,
    RtTextareaComponent,
} from '@rt-tools/ui-kit-v2';
import { AnalyticsService, EAnalyticsGoal } from '@stair-craft/common/analytics';

import { feedbackFormConfig } from '../feedback-form/feedback-form.config';
import { TurnstileWidgetService } from '../turnstile/turnstile-widget.service';
import {
    EContactErrorKind,
    EContactSubmissionStatus,
    EContactTopic,
    IContactFormModel,
    IContactFormSubmitPayload,
} from './contact-form.types';

const TURNSTILE_LOAD_TIMEOUT_MS: number = 5000;
const TIME_TRAP_THRESHOLD_MS: number = 3000;
const FALLBACK_TOKEN_SENTINEL: string = 'fallback:checkbox';

const INITIAL_MODEL: IContactFormModel = {
    name: '',
    email: '',
    phone: '',
    topic: EContactTopic.Calc,
    message: '',
    consent: false,
    captchaToken: '',
    honeypot: '',
};

interface IContactErrorPayload {
    readonly ok?: boolean;
    readonly success?: boolean;
    readonly error?: string;
    readonly message?: string;
    readonly fields?: Record<string, string>;
}

// Сервер отдаёт код отказа строкой. Таблица переводит её в член перечисления, чтобы
// дальше сравнивать однородно, а не строку с перечислением.
const ERROR_KIND_BY_CODE: Readonly<Record<string, EContactErrorKind>> = {
    [EContactErrorKind.RateLimit]: EContactErrorKind.RateLimit,
    [EContactErrorKind.Captcha]: EContactErrorKind.Captcha,
    [EContactErrorKind.Validation]: EContactErrorKind.Validation,
};

// The topics the visitor picks from; the kit list draws them by label.
const TOPIC_OPTIONS: readonly { label: string; value: EContactTopic }[] = [
    { label: $localize`:@@homeContactTopicCalc:Расчёт лестницы`, value: EContactTopic.Calc },
    { label: $localize`:@@homeContactTopicMeasure:Выезд замерщика`, value: EContactTopic.Measure },
    { label: $localize`:@@homeContactTopicCustom:Нестандартная геометрия`, value: EContactTopic.Custom },
    { label: $localize`:@@homeContactTopicMaterials:Консультация по материалам`, value: EContactTopic.Materials },
    { label: $localize`:@@homeContactTopicOther:Другое`, value: EContactTopic.Other },
];

// The site's own field messages, shown by the kit field for every check of that field. The
// length checks are named both ways: the kit's own keys are lowercase, the signal forms' are not.
function fieldErrors(message: string): Readonly<Record<string, string>> {
    return { required: message, email: message, minLength: message, maxLength: message, minlength: message, maxlength: message };
}

const BEM_BLOCK: string = 'sc-contact-form';

@Component({
    selector: 'sc-contact-form',
    templateUrl: './contact-form.component.html',
    styleUrl: './contact-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        FormsModule,
        RouterLink,

        // forms
        FormField,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,

        // rt-tools ui-kit
        RtButtonDirective,
        RtCheckboxComponent,
        RtFieldComponent,
        RtInputComponent,
        RtSelectComponent,
        RtTextareaComponent,
    ],
    providers: [TurnstileWidgetService],
    host: {
        class: BEM_BLOCK,
        '[class.sc-contact-form--no-editorial]': 'hideEditorial()',
        '(focusin)': 'onFocusIn()',
    },
})
export class ContactFormComponent {
    readonly #injector: Injector = inject(Injector);
    readonly #localeId: string = inject(LOCALE_ID);
    readonly #turnstile: TurnstileWidgetService = inject(TurnstileWidgetService);
    readonly #analytics: AnalyticsService = inject(AnalyticsService);

    // Форма живёт на нескольких страницах (home, contacts), поэтому размечает себя сама, а
    // не через outputs родителя (в отличие от sc-feedback-form внутри одной smart-страницы).
    // Флаг — чтобы form_open отправился один раз за жизнь формы, а не на каждый focusin.
    #openedEmitted: boolean = false;

    /**
     * Time the form was rendered. Submissions completed in <3s after this
     * timestamp are treated as bot-like and rejected client-side. Server
     * gets `renderedAt` in the payload for the same trap.
     */
    readonly #renderedAt: number = Date.now();

    protected readonly ContactSubmissionStatus: typeof EContactSubmissionStatus = EContactSubmissionStatus;
    protected readonly topicOptions: readonly { label: string; value: EContactTopic }[] = TOPIC_OPTIONS;
    protected readonly nameErrors: Readonly<Record<string, string>> = fieldErrors($localize`:@@homeContactErrorName:! Укажите имя`);
    protected readonly emailErrors: Readonly<Record<string, string>> = fieldErrors($localize`:@@homeContactErrorEmail:! Проверьте email`);
    protected readonly messageErrors: Readonly<Record<string, string>> = fieldErrors(
        $localize`:@@homeContactErrorMessage:! Опишите задачу подробнее`
    );

    protected readonly captchaContainer: Signal<ElementRef<HTMLElement> | undefined> =
        viewChild<ElementRef<HTMLElement>>('captchaContainer');

    protected readonly model: WritableSignal<IContactFormModel> = signal<IContactFormModel>({ ...INITIAL_MODEL });
    protected form!: FieldTree<IContactFormModel>;

    protected readonly status: WritableSignal<EContactSubmissionStatus> = signal<EContactSubmissionStatus>(EContactSubmissionStatus.Idle);
    protected readonly errorMessage: WritableSignal<string> = signal<string>('');

    /**
     * Set when the Turnstile script fails to render within
     * TURNSTILE_LOAD_TIMEOUT_MS — the UI swaps to a local checkbox that the
     * user clicks to assert humanity. Server-side gating then leans on the
     * honeypot + time-trap that already ride along in the payload.
     */
    protected readonly captchaFallback: WritableSignal<boolean> = signal<boolean>(false);

    protected readonly messageLength: Signal<number> = computed((): number => this.model().message.length);

    protected readonly isBusy: Signal<boolean> = computed((): boolean => this.status() === EContactSubmissionStatus.Submitting);

    protected readonly submitDisabled: Signal<boolean> = computed((): boolean => {
        if (this.isBusy()) {
            return true;
        }
        const m: IContactFormModel = this.model();
        return this.form().invalid() || !m.consent || m.captchaToken.length === 0;
    });

    /**
     * Live-region copy: announces submitting / sent / failed transitions for
     * screen readers. Stays empty in idle state.
     */
    protected readonly liveStatus: Signal<string> = computed((): string => {
        switch (this.status()) {
            case EContactSubmissionStatus.Submitting:
                return $localize`:@@homeContactStatusSending:Отправляем сообщение…`;
            case EContactSubmissionStatus.Success:
                return $localize`:@@homeContactStatusSent:Отправлено. Ответим в течение 24 часов.`;
            case EContactSubmissionStatus.Error:
                return this.errorMessage();
            default:
                return '';
        }
    });

    /**
     * Optional payload context — when provided, gets `JSON.stringify`-d into
     * `config_json`. Mirrors the same input on `sc-feedback-form` so the
     * Worker handles both submissions identically.
     */
    public readonly contextData: InputSignal<unknown> = input<unknown>(null);

    /**
     * When true, the editorial column (eyebrow + h3 + lede + channels) is
     * hidden and the form occupies the full host width. Used on pages that
     * provide their own surrounding hero/intro (e.g. /contacts).
     */
    public readonly hideEditorial: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
        transform: booleanAttribute,
    });

    constructor() {
        runInInjectionContext(this.#injector, (): void => {
            this.form = form(this.model, (schema: SchemaPathTree<IContactFormModel>): void => {
                required(schema.name);
                minLength(schema.name, 2);
                maxLength(schema.name, 80);
                required(schema.email);
                email(schema.email);
                required(schema.message);
                minLength(schema.message, 10);
                maxLength(schema.message, 2000);
                required(schema.captchaToken);

                const whileBusy: () => boolean = (): boolean => this.isBusy();
                disabled(schema.name, { when: whileBusy });
                disabled(schema.email, { when: whileBusy });
                disabled(schema.phone, { when: whileBusy });
                disabled(schema.topic, { when: whileBusy });
                disabled(schema.message, { when: whileBusy });
                disabled(schema.consent, { when: whileBusy });
            });
        });

        afterNextRender((): void => {
            const container: HTMLElement | undefined = this.captchaContainer()?.nativeElement;
            if (container) {
                this.#turnstile.scheduleLoad({
                    container,
                    siteKey: feedbackFormConfig.turnstileSiteKey,
                    localeId: this.#localeId,
                    onToken: (token: string): void => {
                        this.#setCaptchaToken(token);
                    },
                    onLoadTimeout: (): void => {
                        this.captchaFallback.set(true);
                    },
                    loadTimeoutMs: TURNSTILE_LOAD_TIMEOUT_MS,
                });
            }
        });

        effect((): void => {
            const container: ElementRef<HTMLElement> | undefined = this.captchaContainer();
            if (container && !this.#turnstile.isRendered && this.#turnstile.hasRequested && !this.captchaFallback()) {
                this.#turnstile.rerender(container.nativeElement);
            }
        });
    }

    // form_open: первое взаимодействие с формой за её жизнь (host focusin).
    protected onFocusIn(): void {
        if (this.#openedEmitted) {
            return;
        }
        this.#openedEmitted = true;
        this.#analytics.trackGoal(EAnalyticsGoal.ContactOpen);
    }

    protected onHoneypotInput(value: string): void {
        this.model.update((m: IContactFormModel) => ({ ...m, honeypot: value }));
    }

    protected onFallbackCaptchaToggle(checked: boolean): void {
        this.#setCaptchaToken(checked ? FALLBACK_TOKEN_SENTINEL : '');
    }

    protected async onSubmit(event: Event): Promise<void> {
        event.preventDefault();
        if (this.submitDisabled()) {
            return;
        }

        const m: IContactFormModel = this.model();

        if (m.honeypot.length > 0) {
            return;
        }
        if (Date.now() - this.#renderedAt < TIME_TRAP_THRESHOLD_MS) {
            this.#setError(EContactErrorKind.Generic, $localize`:@@homeContactErrorTooFast:Слишком быстрая отправка. Попробуйте ещё раз.`);
            return;
        }

        this.errorMessage.set('');
        this.status.set(EContactSubmissionStatus.Submitting);

        const payload: IContactFormSubmitPayload = {
            email: m.email.trim(),
            message: m.message.trim(),
            'cf-turnstile-response': m.captchaToken,
            subject: 'GoldenCalc contact',
            from_name: 'GoldenCalc',
            name: m.name.trim(),
            phone: m.phone.trim(),
            topic: m.topic,
            locale: this.#localeId,
            honeypot: m.honeypot,
            renderedAt: this.#renderedAt,
        };

        const ctx: unknown = this.contextData();
        if (ctx !== null && ctx !== undefined) {
            payload.config_json = JSON.stringify(ctx, null, 2);
        }

        try {
            const response: Response = await fetch(feedbackFormConfig.feedbackEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(payload),
            });
            const result: IContactErrorPayload = (await response.json().catch((): IContactErrorPayload => ({}))) as IContactErrorPayload;

            const ok: boolean = response.ok && (result.success === true || result.ok === true);
            if (ok) {
                this.status.set(EContactSubmissionStatus.Success);
                this.#analytics.trackGoal(EAnalyticsGoal.ContactSubmitSuccess);
                this.#resetForm();
            } else {
                this.#applyServerError(response.status, result);
            }
        } catch {
            this.#analytics.trackGoal(EAnalyticsGoal.ContactSubmitError, { reason: 'network_error' });
            this.#setError(EContactErrorKind.Generic, $localize`:@@homeContactErrorGeneric:Не удалось отправить. Попробуйте позже.`);
        }
    }

    #applyServerError(httpStatus: number, result: IContactErrorPayload): void {
        const code: string = (result.error ?? '').toLowerCase();
        const kind: EContactErrorKind | undefined = ERROR_KIND_BY_CODE[code];
        const reason: string = code || (httpStatus === 429 ? EContactErrorKind.RateLimit : 'server_error');
        this.#analytics.trackGoal(EAnalyticsGoal.ContactSubmitError, { reason });

        if (kind === EContactErrorKind.RateLimit || httpStatus === 429) {
            this.#setError(
                EContactErrorKind.RateLimit,
                $localize`:@@homeContactErrorRateLimit:Слишком много обращений. Попробуйте через час.`
            );
            return;
        }
        if (kind === EContactErrorKind.Captcha) {
            this.#setError(
                EContactErrorKind.Captcha,
                $localize`:@@homeContactErrorCaptcha:Проверка не пройдена. Подтвердите ещё раз и отправьте снова.`
            );
            return;
        }
        if (kind === EContactErrorKind.Validation) {
            this.#setError(EContactErrorKind.Validation, $localize`:@@homeContactErrorValidation:Проверьте поля формы и попробуйте снова.`);
            return;
        }
        const fallback: string =
            result.message ?? result.error ?? $localize`:@@homeContactErrorGeneric:Не удалось отправить. Попробуйте позже.`;
        this.#setError(EContactErrorKind.Generic, fallback);
    }

    #setError(kind: EContactErrorKind, message: string): void {
        this.status.set(EContactSubmissionStatus.Error);
        this.errorMessage.set(message);

        if (kind !== EContactErrorKind.RateLimit && kind !== EContactErrorKind.Validation) {
            this.#turnstile.reset();
        }
    }

    #setCaptchaToken(token: string): void {
        this.model.update((m: IContactFormModel) => ({ ...m, captchaToken: token }));
    }

    #resetForm(): void {
        this.#turnstile.reset();
        this.model.set({ ...INITIAL_MODEL });
        this.form().reset(this.model());
    }
}
